import type { ExpectedDebitsBand, ExpectedDebitsTable, Gender, Relation, RelationClass } from "../types";

export type { RelationClass } from "../types";

export interface ExpectedAndBase {
  expected: number;
  base: number;
}

export function relationClassOf(relation: Relation): RelationClass {
  switch (relation) {
    case "Employee":
      return "Applicant";
    case "Spouse":
    case "Dependent Adult":
      return "SpouseOrDependentAdult";
    case "Child":
      return "Child";
    default: {
      const exhaustive: never = relation;
      throw new Error(`Unknown relation ${String(exhaustive)}`);
    }
  }
}

function columnFor(band: ExpectedDebitsBand, gender: Gender | null): ExpectedAndBase {
  if (gender === "M") return { expected: band.maleExpected, base: band.maleBase };
  if (gender === "F") return { expected: band.femaleExpected, base: band.femaleBase };
  return { expected: band.compositeExpected, base: band.compositeBase };
}

function meanOf(values: readonly ExpectedAndBase[]): ExpectedAndBase {
  const expected = values.reduce((sum, value) => sum + value.expected, 0) / values.length;
  const base = values.reduce((sum, value) => sum + value.base, 0) / values.length;
  return { expected, base };
}

/**
 * Expected and Base debits lookup (product plan 4.6). Missing gender uses the
 * Composite column; missing age uses the all-age mean for that relation class
 * and gender; ages beyond the last band clamp to it (a 200-year-old Gradient Doe
 * is an adult in the max band).
 */
export function expectedAndBase(table: ExpectedDebitsTable, relationClass: RelationClass, age: number | null, gender: Gender | null): ExpectedAndBase {
  const bands = table.bands.filter((band) => band.relationClass === relationClass).slice().sort((a, b) => a.ageStart - b.ageStart);
  if (bands.length === 0) return { expected: 0, base: 0 };
  if (age === null) return meanOf(bands.map((band) => columnFor(band, gender)));

  const match = bands.find((band) => age >= band.ageStart && age <= band.ageEnd);
  if (match) return columnFor(match, gender);
  const last = bands[bands.length - 1];
  if (age > last.ageEnd) return columnFor(last, gender);
  return columnFor(bands[0], gender);
}

export interface BandValidationIssue {
  relationClass: RelationClass;
  message: string;
}

/** Bands per relation class must start at 0, be contiguous, and not overlap (product plan 6.9). */
export function validateBandContiguity(bands: readonly Pick<ExpectedDebitsBand, "relationClass" | "ageStart" | "ageEnd">[]): BandValidationIssue[] {
  const issues: BandValidationIssue[] = [];
  const classes: RelationClass[] = ["Applicant", "SpouseOrDependentAdult", "Child"];
  for (const relationClass of classes) {
    const sorted = bands.filter((band) => band.relationClass === relationClass).slice().sort((a, b) => a.ageStart - b.ageStart);
    if (sorted.length === 0) {
      issues.push({ relationClass, message: "No age bands entered." });
      continue;
    }
    if (sorted[0].ageStart !== 0) issues.push({ relationClass, message: `First band starts at ${sorted[0].ageStart}; it must start at 0.` });
    for (let index = 0; index < sorted.length; index += 1) {
      const band = sorted[index];
      if (band.ageEnd < band.ageStart) issues.push({ relationClass, message: `Band ${band.ageStart} to ${band.ageEnd} ends before it starts.` });
      const next = sorted[index + 1];
      if (!next) continue;
      if (next.ageStart <= band.ageEnd) issues.push({ relationClass, message: `Bands ${band.ageStart} to ${band.ageEnd} and ${next.ageStart} to ${next.ageEnd} overlap.` });
      else if (next.ageStart !== band.ageEnd + 1) issues.push({ relationClass, message: `Gap between age ${band.ageEnd} and ${next.ageStart}.` });
    }
  }
  return issues;
}
